<script setup>
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { administrationTypeColor, DOSE_AMOUNT_OPTIONS, todayIn } from '@/common/medicationSchedule';
import {
    ascendingSteps,
    batchProblems,
    buildPresetMedicationRequests,
    defaultPassTimeCount,
    fillFromPassTimes,
    presetApplyBundle,
    presetApplyRows,
    rowNotes,
    rowProblems,
    siteRequired,
} from '@/common/medicationPresetApply';
import { accountAdministrationWindow, enrolledSiteOf, usesDayOfWeek } from '@/common/medicationWindow';
import { requiresMedicationReason } from '@/common/medicationPreferences';
import { chainTaperSteps, pruneAcknowledged, taperStepRange } from '@/common/medicationTaper';
import BnChip from '@/components/BnChip.vue';
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnDayOfWeekToggle from '@/components/BnDayOfWeekToggle.vue';
import BnTimePicker from '@/components/BnTimePicker.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import { administrationTypeOptions } from '@/composables/useMedicationOrderEditor';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useMedicationPresets } from '@/composables/useMedicationPresets';
import { usePrescriberPicker } from '@/composables/usePrescriberPicker';
import { formatDate } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, reactive, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    patientId: {
        type: String,
        required: true,
    },
    // Where this patient's medications can be given, from useAdministrationSites.
    sites: {
        type: Array,
        default: () => [],
    },
    sitesLoading: {
        type: Boolean,
        default: false,
    },
    timeZone: {
        type: String,
        default: undefined,
    },
    // The medication list's own duplicate rule, so the flag here and the add dialog's question agree.
    findDuplicate: {
        type: Function,
        required: true,
    },
    // A recorded "no medications" statement, cleared in the same transaction.
    nilKnownListId: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue', 'applied']);

const authStore = useAuthStore();
const vuetifyDisplay = useDisplay();

// Where the eMAR has not been released nothing reads a schedule, a taper or a site, so none is asked.
const emarEnabled = useFeatureFlags().isEnabled('emar');

const { loadActivePresets } = useMedicationPresets({ enabled: false });

const presets = ref([]);
const presetsLoading = ref(false);
const presetsError = ref(false);
const presetId = ref(null);
const rows = ref([]);
const start = ref(null);
const siteId = ref(null);
const passTimes = ref([]);
// Per row key, the ascending step indexes the user acknowledged. Never stored on the preset.
const acknowledged = ref(new Map());
// Pickers whose typed time could not be read: the model keeps its previous value, so that time
// counts as missing and its row is added without a schedule, never with a time the field is not showing.
const unreadableTimes = ref(new Set());
const saving = ref(false);
const saveError = ref('');

// The batch prescriber, held the way the add dialog holds it on the medication.
const prescriberHolder = reactive({ requester: undefined });
const prescriberDisplay = computed(() => prescriberHolder.requester?.display || '');
const { prescriberMenu, prescriberMatches, onPrescriberFocused, onPrescriberSearch, setPrescriber } = usePrescriberPicker({
    target: () => prescriberHolder,
    display: () => prescriberDisplay.value,
});

const isOpen = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const today = computed(() => todayIn(props.timeZone));

const selectedPreset = computed(() => presets.value.find((preset) => preset.id === presetId.value) || null);

const presetItems = computed(() => presets.value.map((preset) => ({ value: preset.id, title: preset.name })));

const enrolledSite = computed(() => enrolledSiteOf(props.sites));

const isSiteRequired = computed(() => siteRequired({ emarEnabled, sites: props.sites }));

const typeChip = (type) => administrationTypeOptions.find((option) => option.value === type)?.chip || '';

const medicationName = (row) => row.item?.medication?.text || row.item?.medication?.coding?.[0]?.display || '';

const doseUnit = (row) => row.item?.dose?.unit || '';

const isScheduled = (window) => (window?.type || 'scheduled') === 'scheduled';

const acknowledgedFor = (row) => acknowledged.value.get(row.key) || new Set();

// --- fills: every one happens in the handler that changed its input, never in a watcher on the rows

const fillRow = (row) => ({
    ...row,
    window: row.window ? fillFromPassTimes(row.window, passTimes.value) : row.window,
    steps: row.steps ? row.steps.map((step) => fillFromPassTimes(step, passTimes.value)) : row.steps,
});

const refillRows = () => {
    rows.value = rows.value.map(fillRow);
};

const patchRow = (rowIndex, patch) => {
    rows.value = rows.value.map((row, index) => (index === rowIndex ? { ...row, ...patch } : row));
};

// stepIndex null addresses a Scheduled row's own window.
const patchWindow = (rowIndex, stepIndex, patch) => {
    const row = rows.value[rowIndex];
    if (stepIndex === null) {
        patchRow(rowIndex, { window: patch(row.window) });
        return;
    }
    patchRow(rowIndex, { steps: row.steps.map((step, index) => (index === stepIndex ? patch(step) : step)) });
};

const seedRows = () => {
    const preset = selectedPreset.value;
    rows.value = preset ? presetApplyRows(preset, { emarEnabled, accountWindow: accountAdministrationWindow(authStore.account) }) : [];
    passTimes.value = emarEnabled ? Array.from({ length: defaultPassTimeCount(rows.value) }, () => null) : [];
    acknowledged.value = new Map();
    unreadableTimes.value = new Set();
    refillRows();
};

const choosePreset = (id) => {
    presetId.value = id;
    seedRows();
};

const setPassTime = (index, value) => {
    passTimes.value = passTimes.value.map((time, position) => (position === index ? value || null : time));
    refillRows();
};

const addPassTime = () => {
    passTimes.value = [...passTimes.value, null];
    refillRows();
};

const passKey = (index) => `pass${index}`;

const timeKey = (rowIndex, stepIndex, slot) => (stepIndex === null ? `row${rowIndex}time${slot}` : `row${rowIndex}step${stepIndex}time${slot}`);

const removePassTime = (index) => {
    passTimes.value = passTimes.value.filter((time, position) => position !== index);
    // A flag left on a pass time that no longer exists would leave rows unscheduled.
    const next = new Set([...unreadableTimes.value].filter((key) => !key.startsWith('pass')));
    passTimes.value.forEach((time, position) => {
        const was = position >= index ? position + 1 : position;
        if (unreadableTimes.value.has(passKey(was))) next.add(passKey(position));
    });
    unreadableTimes.value = next;
    refillRows();
};

const setRowChecked = (rowIndex, checked) => {
    patchRow(rowIndex, { checked: checked === true });
    rows.value = rows.value.map((row, index) => (index === rowIndex ? fillRow(row) : row));
};

const setRowDose = (rowIndex, value) => {
    patchRow(rowIndex, { doseValue: value ?? null });
    // An acknowledgement covers the ascending step the user was shown, not a standing waiver.
    const row = rows.value[rowIndex];
    const current = acknowledgedFor(row);
    const kept = pruneAcknowledged(current, ascendingSteps(row));
    if (kept !== current) acknowledged.value = new Map(acknowledged.value).set(row.key, kept);
};

const setRowInstructions = (rowIndex, value) => {
    patchRow(rowIndex, { patientInstruction: value || '' });
};

// An account preference: each checked row then needs a reason, prefilled from the preset.
const requireReason = computed(() => requiresMedicationReason(authStore.account));

const setRowReason = (rowIndex, value) => {
    patchRow(rowIndex, { reasonText: value || '' });
};

const setWindowTime = (rowIndex, stepIndex, slot, value) => {
    patchWindow(rowIndex, stepIndex, (window) => ({ ...window, times: window.times.map((time, index) => (index === slot ? value || null : time)), timesEdited: true }));
};

// Choosing days changes how many times each day needs, so the window refills (or, once its
// times were typed, resizes).
const setWindowDays = (rowIndex, stepIndex, days) => {
    patchWindow(rowIndex, stepIndex, (window) => fillFromPassTimes({ ...window, dayOfWeek: days || [] }, passTimes.value));
    const row = rows.value[rowIndex];
    const slots = (stepIndex === null ? row.window : row.steps[stepIndex]).times.length;
    // A flag left on a slot the resize removed would leave the row unscheduled.
    const prefix = timeKey(rowIndex, stepIndex, '');
    const kept = [...unreadableTimes.value].filter((key) => !key.startsWith(prefix) || Number(key.slice(prefix.length)) < slots);
    if (kept.length !== unreadableTimes.value.size) unreadableTimes.value = new Set(kept);
};

const setTitrationAcknowledged = (row, stepIndex, value) => {
    const next = new Set(acknowledgedFor(row));
    if (value) next.add(stepIndex);
    else next.delete(stepIndex);
    acknowledged.value = new Map(acknowledged.value).set(row.key, next);
};

const setTimeInvalid = (key, invalid) => {
    const next = new Set(unreadableTimes.value);
    if (invalid) next.add(key);
    else next.delete(key);
    unreadableTimes.value = next;
};

// --- derived state

const showPassTimes = computed(() => emarEnabled && rows.value.some((row) => row.checked && [row.window, ...(row.steps || [])].some((window) => window && isScheduled(window))));

// An unreadable time is missing: its own slot, or an un-edited slot filled from an unreadable pass time.
const readableWindow = (window, rowIndex, stepIndex) => {
    if (!window || !isScheduled(window) || !unreadableTimes.value.size) return window;
    const unreadable = (slot) => unreadableTimes.value.has(timeKey(rowIndex, stepIndex, slot)) || (!window.timesEdited && unreadableTimes.value.has(passKey(slot)));
    return { ...window, times: (window.times || []).map((time, slot) => (unreadable(slot) ? null : time)) };
};

// The rows as Save would read them.
const savedRows = computed(() =>
    rows.value.map((row, rowIndex) => ({
        ...row,
        window: readableWindow(row.window, rowIndex, null),
        steps: row.steps ? row.steps.map((step, stepIndex) => readableWindow(step, rowIndex, stepIndex)) : row.steps,
    })),
);

const problemsByRow = computed(() => savedRows.value.map((row) => rowProblems(row, { start: start.value, acknowledged: acknowledgedFor(row), requireReason: requireReason.value })));

const notesByRow = computed(() => savedRows.value.map((row) => rowNotes(row)));

const ascendingByRow = computed(() => rows.value.map((row) => ascendingSteps(row)));

const duplicateByRow = computed(() =>
    rows.value.map((row) => {
        if (!emarEnabled || (row.type !== 'scheduled' && row.type !== 'taper')) return null;
        return props.findDuplicate({ medication: { CodeableConcept: row.item?.medication } }) || null;
    }),
);

// Each step's span from the batch start, by the same rechainTaper the save uses.
const stepRangesByRow = computed(() =>
    rows.value.map((row) => chainTaperSteps(row.steps, start.value, { afterFinal: row.item?.afterFinal }).map((shell) => taperStepRange(shell.timing?.repeat?.boundsPeriod, formatDate))),
);

const batch = computed(() =>
    batchProblems({
        rows: rows.value,
        start: start.value,
        today: today.value,
        emarEnabled,
        sites: props.sites,
        siteId: siteId.value,
        sitesLoading: props.sitesLoading,
    }),
);

const canSave = computed(() => Boolean(selectedPreset.value) && !saving.value && !Object.keys(batch.value).length && problemsByRow.value.every((problems) => !problems.length));

// --- open, load, save

const loadPresets = async () => {
    presetsLoading.value = true;
    presetsError.value = false;
    try {
        presets.value = await loadActivePresets();
        if (presets.value.length === 1) choosePreset(presets.value[0].id);
    } catch (error) {
        console.error('BnMedicationPresetApplyDialog: could not load medication presets', { patientId: props.patientId, error });
        presets.value = [];
        presetsError.value = true;
    } finally {
        presetsLoading.value = false;
    }
};

const reset = () => {
    presets.value = [];
    presetId.value = null;
    rows.value = [];
    passTimes.value = [];
    acknowledged.value = new Map();
    unreadableTimes.value = new Set();
    start.value = today.value;
    siteId.value = emarEnabled ? enrolledSite.value : null;
    prescriberHolder.requester = undefined;
    saveError.value = '';
    saving.value = false;
    loadPresets();
};

watch(
    () => props.modelValue,
    (open) => {
        if (open) reset();
    },
    { immediate: true },
);

// The enrollments may still be reading when the dialog opens, so the default lands on whichever
// resolves second - never over an answer already there.
watch(enrolledSite, (id) => {
    if (props.modelValue && emarEnabled && id && !siteId.value) siteId.value = id;
});

const close = () => {
    emit('update:modelValue', false);
};

const save = async () => {
    if (!canSave.value) return;
    saving.value = true;
    saveError.value = '';
    let posted = false;
    let medications = [];
    try {
        medications = buildPresetMedicationRequests({
            preset: selectedPreset.value,
            rows: savedRows.value,
            start: start.value,
            requester: prescriberHolder.requester,
            siteId: emarEnabled ? siteId.value : null,
            patientId: props.patientId,
            practitioner: authStore.practitioner,
            emarEnabled,
            timeZone: props.timeZone,
            now: new Date().toISOString(),
        });
        posted = true;
        // One transaction: Aidbox writes all of it or none of it.
        await $httpFhirApi.post('/', presetApplyBundle(medications, { nilKnownListId: props.nilKnownListId }));
    } catch (error) {
        console.error('BnMedicationPresetApplyDialog: could not add medications from a preset', { patientId: props.patientId, presetId: presetId.value, count: medications.length, error });
        // With no response the transaction may still have landed.
        saveError.value =
            posted && !error?.response ? 'The save may not have finished. Check the medication list before trying again.' : 'The medications could not be added. Nothing was saved. Try again.';
        return;
    } finally {
        saving.value = false;
    }

    emit('applied', { count: medications.length, presetName: selectedPreset.value?.name || '' });
    close();
};
</script>

<template>
    <v-dialog v-model="isOpen" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
        <v-card data-cy="medicationPresetApplyDialog" class="d-flex flex-column">
            <v-toolbar density="compact" flat class="flex-grow-0">
                <v-toolbar-title>Add from preset</v-toolbar-title>
                <BnToolbarBtn type="secondary" label="Discard" icon="trash" data-cy="presetApplyCancel" @click="close" />
                <BnToolbarBtn type="primary" right label="Save" icon="check" :disabled="!canSave" :loading="saving" data-cy="presetApplySave" @click="save" />
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mb-4" data-cy="presetApplyError">{{ saveError }}</v-alert>

                <div v-if="presetsLoading" class="text-caption text-medium-emphasis" data-cy="presetApplyLoading">
                    <FontAwesomeIcon :icon="['far', 'spinner']" class="fa-spin mr-1" />
                    Loading presets...
                </div>

                <v-alert v-else-if="presetsError" type="error" variant="tonal" density="compact" data-cy="presetApplyLoadError">
                    The presets could not be loaded.
                    <template #append>
                        <v-btn variant="tonal" color="primary" data-cy="presetApplyRetry" @click="loadPresets">Try again</v-btn>
                    </template>
                </v-alert>

                <div v-else-if="!presets.length" class="text-body-2 text-medium-emphasis" data-cy="presetApplyNoPresets">No active presets</div>

                <template v-else>
                    <v-select :model-value="presetId" :items="presetItems" label="Preset" data-cy="presetApplyPresetSelect" @update:model-value="choosePreset"></v-select>

                    <template v-if="selectedPreset">
                        <div class="d-flex flex-wrap align-start ga-2">
                            <div style="width: 150px">
                                <BnDatePicker
                                    v-model="start"
                                    :strict-date="false"
                                    :error-messages="batch.start ? [batch.start] : []"
                                    label="Effective start"
                                    data-cy="presetApplyStartDate"
                                ></BnDatePicker>
                            </div>

                            <!-- One site for the batch, as the taper dialog asks one for a course. -->
                            <v-select
                                v-if="emarEnabled && sites.length"
                                v-model="siteId"
                                :items="sites"
                                :loading="sitesLoading"
                                item-title="label"
                                item-value="id"
                                :clearable="!isSiteRequired"
                                :error-messages="batch.site ? [batch.site] : []"
                                label="Administration site"
                                class="flex-grow-1"
                                style="min-width: 220px"
                                data-cy="presetApplySite"
                            ></v-select>
                        </div>

                        <!-- The add dialog's Prescriber: active in-house providers, or free text for an outside prescriber. -->
                        <v-combobox
                            v-model:menu="prescriberMenu"
                            :model-value="prescriberDisplay"
                            :items="prescriberMatches"
                            no-filter
                            item-title="fullName"
                            return-object
                            label="Prescriber"
                            data-cy="presetApplyPrescriberField"
                            @update:focused="onPrescriberFocused"
                            @update:search="onPrescriberSearch"
                            @update:model-value="(value) => setPrescriber(value, prescriberHolder)"
                        >
                            <template #item="{ item, props: itemProps }">
                                <v-list-item v-bind="itemProps" :title="null">
                                    <v-list-item-title data-cy="presetApplyPrescriberSearchItem">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-combobox>

                        <div v-if="showPassTimes" class="mb-2" data-cy="presetApplyPassTimes">
                            <div class="text-subtitle-2">Pass times</div>
                            <div class="text-caption text-medium-emphasis mb-2">
                                Each medication takes its times in order: one given twice a day takes pass times 1 and 2. You can change any medication's times below.
                            </div>
                            <div class="d-flex flex-wrap align-center">
                                <div v-for="(time, index) in passTimes" :key="index" class="d-flex align-center mr-1">
                                    <!-- Wider than a row's Time field: 'Pass time N' and the clock
                                         button both have to fit without the label truncating. -->
                                    <div class="flex-shrink-0" style="width: 160px">
                                        <BnTimePicker
                                            :model-value="time"
                                            :label="`Pass time ${index + 1}`"
                                            :minute-step="5"
                                            :data-cy="`presetApplyPassTime${index}`"
                                            @update:model-value="(value) => setPassTime(index, value)"
                                            @update:invalid="(value) => setTimeInvalid(passKey(index), value)"
                                        ></BnTimePicker>
                                    </div>
                                    <v-btn
                                        v-if="passTimes.length > 1"
                                        variant="text"
                                        size="small"
                                        color="secondary"
                                        class="mb-5 px-0"
                                        style="min-width: 24px"
                                        :aria-label="`Remove pass time ${index + 1}`"
                                        :data-cy="`presetApplyRemovePassTime${index}`"
                                        @click="removePassTime(index)"
                                    >
                                        <FontAwesomeIcon :icon="['far', 'trash']" />
                                    </v-btn>
                                </div>
                                <v-btn variant="text" color="primary" class="mb-5" data-cy="presetApplyAddPassTime" @click="addPassTime">
                                    <FontAwesomeIcon :icon="['far', 'plus']" class="mr-1" />
                                    Add pass time
                                </v-btn>
                            </div>
                        </div>

                        <div v-if="batch.none" class="text-caption text-error mb-2" data-cy="presetApplyNone">{{ batch.none }}</div>

                        <v-card v-for="(row, rowIndex) in rows" :key="row.key" variant="outlined" class="mt-3 pa-3" :data-cy="`presetApplyRow${rowIndex}`">
                            <div class="d-flex align-center flex-wrap ga-2">
                                <v-checkbox
                                    :model-value="row.checked"
                                    :disabled="row.incomplete"
                                    :aria-label="`Add ${medicationName(row)}`"
                                    density="compact"
                                    hide-details
                                    class="flex-grow-0"
                                    :data-cy="`presetApplyRow${rowIndex}Checkbox`"
                                    @update:model-value="(value) => setRowChecked(rowIndex, value)"
                                ></v-checkbox>
                                <div class="text-body-2 font-weight-bold">{{ medicationName(row) }}</div>
                                <BnChip native size="small" :color="administrationTypeColor(row.type)">{{ typeChip(row.type) }}</BnChip>
                                <v-tooltip v-if="duplicateByRow[rowIndex]" location="top" max-width="320">
                                    <template #activator="{ props: tipProps }">
                                        <BnChip v-bind="tipProps" native size="small" color="warning" :data-cy="`presetApplyRow${rowIndex}Duplicate`">Already being given</BnChip>
                                    </template>
                                    {{ duplicateByRow[rowIndex].medication?.CodeableConcept?.text }} is already on this patient's list and administered by the clinic. Adding this one as well means
                                    both will appear in the pass.
                                </v-tooltip>
                            </div>

                            <div v-if="row.incomplete" class="text-caption text-medium-emphasis mt-1" :data-cy="`presetApplyRow${rowIndex}Incomplete`">
                                This medication is incomplete in the preset.
                            </div>

                            <template v-else>
                                <div class="d-flex flex-wrap mt-2">
                                    <v-combobox
                                        :model-value="row.doseValue"
                                        :items="DOSE_AMOUNT_OPTIONS"
                                        :disabled="!row.checked"
                                        :suffix="doseUnit(row)"
                                        label="Dose amount"
                                        class="mr-2 flex-grow-0 flex-shrink-0"
                                        style="width: 170px"
                                        :data-cy="`presetApplyRow${rowIndex}Dose`"
                                        @update:model-value="(value) => setRowDose(rowIndex, value)"
                                    ></v-combobox>
                                    <v-text-field
                                        :model-value="row.patientInstruction"
                                        :disabled="!row.checked"
                                        label="Administration instructions"
                                        class="flex-grow-1"
                                        style="min-width: 200px"
                                        :data-cy="`presetApplyRow${rowIndex}Instructions`"
                                        @update:model-value="(value) => setRowInstructions(rowIndex, value)"
                                    ></v-text-field>
                                </div>
                                <v-text-field
                                    v-if="requireReason"
                                    :model-value="row.reasonText"
                                    :disabled="!row.checked"
                                    label="Reason for taking medication"
                                    :class="{ 'bn-required-field': row.checked && !row.reasonText?.trim() }"
                                    :data-cy="`presetApplyRow${rowIndex}Reason`"
                                    @update:model-value="(value) => setRowReason(rowIndex, value)"
                                ></v-text-field>

                                <!-- A Scheduled row: its times and, on a week interval, its days. -->
                                <template v-if="row.window">
                                    <div class="text-caption text-medium-emphasis">Administration times</div>
                                    <div class="d-flex flex-wrap align-center">
                                        <div v-for="(time, slot) in row.window.times" :key="slot" class="flex-shrink-0 mr-1" style="width: 145px">
                                            <BnTimePicker
                                                :model-value="time"
                                                :disabled="!row.checked"
                                                :label="`Time ${slot + 1}`"
                                                :minute-step="5"
                                                :data-cy="`presetApplyRow${rowIndex}Time${slot}`"
                                                @update:model-value="(value) => setWindowTime(rowIndex, null, slot, value)"
                                                @update:invalid="(value) => setTimeInvalid(timeKey(rowIndex, null, slot), value)"
                                            ></BnTimePicker>
                                        </div>
                                    </div>
                                    <div v-if="usesDayOfWeek(row.window)" class="mb-2" :data-cy="`presetApplyRow${rowIndex}DayOfWeek`">
                                        <div class="text-caption text-medium-emphasis">Days of week</div>
                                        <BnDayOfWeekToggle
                                            :model-value="row.window.dayOfWeek"
                                            :disabled="!row.checked"
                                            @update:model-value="(value) => setWindowDays(rowIndex, null, value)"
                                        ></BnDayOfWeekToggle>
                                    </div>
                                </template>

                                <!-- A Taper row: one compact block per step. -->
                                <template v-if="row.steps">
                                    <div v-for="(step, stepIndex) in row.steps" :key="stepIndex" class="mt-2 pl-3 border-s-md" :data-cy="`presetApplyRow${rowIndex}Step${stepIndex}`">
                                        <div class="d-flex flex-wrap align-center ga-2">
                                            <div class="text-subtitle-2">Step {{ stepIndex + 1 }}</div>
                                            <div v-if="stepRangesByRow[rowIndex]?.[stepIndex]" class="text-caption text-medium-emphasis">
                                                {{ stepRangesByRow[rowIndex][stepIndex] }}
                                            </div>
                                            <BnChip v-if="!isScheduled(step)" native size="small" :color="step.type === 'prn' ? administrationTypeColor('prn') : 'warning'">
                                                {{ step.type === 'prn' ? 'PRN' : 'Hold' }}
                                            </BnChip>
                                        </div>
                                        <template v-if="isScheduled(step)">
                                            <div class="d-flex flex-wrap align-center">
                                                <div v-for="(time, slot) in step.times" :key="slot" class="flex-shrink-0 mr-1" style="width: 145px">
                                                    <BnTimePicker
                                                        :model-value="time"
                                                        :disabled="!row.checked"
                                                        :label="`Time ${slot + 1}`"
                                                        :minute-step="5"
                                                        :data-cy="`presetApplyRow${rowIndex}Step${stepIndex}Time${slot}`"
                                                        @update:model-value="(value) => setWindowTime(rowIndex, stepIndex, slot, value)"
                                                        @update:invalid="(value) => setTimeInvalid(timeKey(rowIndex, stepIndex, slot), value)"
                                                    ></BnTimePicker>
                                                </div>
                                            </div>
                                            <div v-if="usesDayOfWeek(step)" class="mb-2" :data-cy="`presetApplyRow${rowIndex}Step${stepIndex}DayOfWeek`">
                                                <div class="text-caption text-medium-emphasis">Days of week</div>
                                                <BnDayOfWeekToggle
                                                    :model-value="step.dayOfWeek"
                                                    :disabled="!row.checked"
                                                    @update:model-value="(value) => setWindowDays(rowIndex, stepIndex, value)"
                                                ></BnDayOfWeekToggle>
                                            </div>
                                        </template>
                                        <template v-if="ascendingByRow[rowIndex].includes(stepIndex)">
                                            <v-alert type="warning" variant="tonal" density="compact" class="mt-2" :data-cy="`presetApplyRow${rowIndex}Step${stepIndex}TitrationWarning`">
                                                This step raises the total daily dose. Every step of a taper runs automatically on its date with nobody deciding, so this increase would happen without
                                                a clinician reviewing the patient first.
                                            </v-alert>
                                            <v-checkbox
                                                :model-value="acknowledgedFor(row).has(stepIndex)"
                                                :disabled="!row.checked"
                                                label="I understand this increase will run automatically"
                                                density="comfortable"
                                                hide-details
                                                :data-cy="`presetApplyRow${rowIndex}Step${stepIndex}TitrationAcknowledge`"
                                                @update:model-value="(value) => setTitrationAcknowledged(row, stepIndex, value === true)"
                                            ></v-checkbox>
                                        </template>
                                    </div>
                                </template>

                                <div v-if="row.type === 'taper' && !emarEnabled" class="text-caption text-medium-emphasis mt-1" :data-cy="`presetApplyRow${rowIndex}TaperOrderOnly`">
                                    Taper steps are added once the eMAR is available — open the medication and set the taper.
                                </div>

                                <div v-if="notesByRow[rowIndex].length" class="text-caption text-medium-emphasis mt-2" :data-cy="`presetApplyRow${rowIndex}Unscheduled`">
                                    <div v-for="(note, noteIndex) in notesByRow[rowIndex]" :key="noteIndex">{{ note }}</div>
                                </div>

                                <div v-if="problemsByRow[rowIndex].length" class="text-caption text-error mt-2" :data-cy="`presetApplyRow${rowIndex}Problems`">
                                    <div v-for="(problem, problemIndex) in problemsByRow[rowIndex]" :key="problemIndex">{{ problem }}</div>
                                </div>
                            </template>
                        </v-card>
                    </template>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
